import {
  DefaultFetchError,
  FetchCreate,
  FetchDelete,
  FetchGet,
  FetchGetId,
  FetchSuccess,
  FetchUpdate,
  PagingDataResponse,
  PagingParams,
  useFetchCreate,
  useFetchDelete,
  useFetchGet,
  useFetchGetId,
  useFetchUpdate,
} from "../fetch";
import { User } from "../../store/auth";
import { LogicCondition } from "../../types/survey";
import { UploadFile } from "antd";

export enum SurveyStatus {
  DRAFT = "draft",
  PUBLISHED = "published",
}

export const SurveyStatusDisplay = {
  [SurveyStatus.DRAFT]: "Draft",
  [SurveyStatus.PUBLISHED]: "Published",
};

export enum SurveyStatusColor {
  "draft" = "#1677FF",
  "published" = "#52C41A",
  "archived" = "gray",
}

export interface ApiQuestionOptions {
  multiline?: boolean;
  minLength?: number;
  maxLength?: number;
  enabled?: boolean;
}

export interface ApiQuestionData {
  id?: string;
  title: string;
  sort: number;
  type: string;
  options: ApiQuestionOptions;
  logic?: LogicCondition[];
  answers?: ApiAnswerData[];
  files?: UploadFile[];
}

export interface ApiLogicCondition {
  when: {
    op: string;
    value: boolean;
  };
  actions: {
    type: string;
    targetFieldId: string;
  }[];
}

export interface ApiAnswerData {
  id?: string;
  title: string;
  sort: number;
  options?: ApiQuestionOptions;
  logic?: LogicCondition[];
}

export interface ApiSectionData {
  id?: string;
  title: string;
  sort: number;
  questions: ApiQuestionData[];
  isRepeatable?: boolean;
}

export interface ApiSurveyItem {
  id?: string;
  sort: number;
  question: ApiQuestionData | null;
  section: ApiSectionData | null;
}

export interface SurveyCreateFullParams {
  title: string;
  orderId?: string;
  status?: SurveyStatus;
  items: ApiSurveyItem[];
}

export interface SurveyCreateFullResponse {
  id: string;
  title: string;
  orderId?: string;
  status: SurveyStatus;
  items: ApiSurveyItem[];
  createdAt: string;
  updatedAt: string;
}

export interface SurveyTemplate extends Survey {
  items: ApiSurveyItem[];
}

export interface SurveyTemplateTableRow {
  key: string;
  id: string;
  title: string;
  status: SurveyStatus;
  // createdBy: string;
  userName: string;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  user?: User;
}

export interface SurveyTemplatesTableData {
  data: SurveyTemplateTableRow[];
  total: number;
}

export interface Survey {
  id: string;
  title: string;
  jobs?: Jobs[];
  user: User;
  createdAt: string;
  updatedAt: string;
  status: SurveyStatus;
}

export interface SurveysGetParams extends PagingParams {
  title?: string;
  orderId?: string;
  status?: string;
  createdBy?: string;
  createdFrom?: string;
  createdTo?: string;
}

export interface SurveyCreateParams {
  title: string;
  orderId?: string;
  status?: SurveyStatus;
}

export interface SurveyUpdateParams extends Partial<SurveyCreateParams> {
  id?: string;
}

// "jobs": [
//         {
//           "id": "string",
//           "jobId": "string",
//           "title": "string"
//         }
//       ]
export interface Jobs {
  id: string;
  jobId: string;
  title: string;
}

export interface SurveyTableRow {
  key: string;
  id: string;
  title: string;
  jobs?: Jobs[];
  orderId?: string;
  userName: string;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  status: SurveyStatus;
  user?: User;
}

export interface SurveysTableData {
  data: SurveyTableRow[];
  total: number;
}

// ==================== SURVEY HOOKS ====================

export const useSurveysGetAll = <DD = SurveysTableData>(
  decorateData?: (data: PagingDataResponse<Survey>) => DD,
): FetchGet<
  PagingDataResponse<Survey>,
  SurveysGetParams,
  DefaultFetchError,
  DD
> =>
  useFetchGet("survey", {
    autoStart: false,
    startStateLoading: false,
    decorateData,
  });

export const useSurveysTableGet = () =>
  useSurveysGetAll<SurveysTableData>(
    ({ data, meta }: PagingDataResponse<Survey>): SurveysTableData => ({
      data: data.map(
        (item: Survey): SurveyTableRow => ({
          key: item.id,
          id: item.id,
          title: item.title,
          jobs: item.jobs,
          userName:
            `${item.user?.firstName || ""} ${item.user?.lastName || ""}`.trim() ||
            "-",
          createdById: item.user?.id || "",
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
          status: item.status,
          user: item.user,
        }),
      ),
      total: meta.itemCount,
    }),
  );

export interface SurveyDetail extends Survey {
  items: ApiSurveyItem[];
}

export const useSurveyGetById = <DD = SurveyDetail>(
  id?: string,
  decorateData?: (data: SurveyDetail) => DD,
): FetchGetId<SurveyDetail, DefaultFetchError, unknown, DD> =>
  useFetchGetId("survey", id, {
    autoStart: !!id,
    decorateData,
  });

export const useSurveyUpdate = (
  id?: string,
): FetchUpdate<Survey, DefaultFetchError, SurveyUpdateParams> =>
  useFetchUpdate("survey", id);

export const useSurveyDelete = (
  id?: string,
): FetchDelete<FetchSuccess, DefaultFetchError> => useFetchDelete("survey", id);

export const useSurveysGet = (): FetchGet<
  PagingDataResponse<Survey>,
  SurveysGetParams
> =>
  useFetchGet("survey", {
    autoStart: false,
    startStateLoading: false,
  });

export const useSurveyCreate = (): FetchCreate<
  Survey,
  DefaultFetchError,
  SurveyCreateParams
> => useFetchCreate("survey");

export const useSurveyCreateFull = (): FetchCreate<
  SurveyCreateFullResponse,
  DefaultFetchError,
  SurveyCreateFullParams
> => useFetchCreate("survey");

// ==================== TEMPLATE HOOKS ====================

export const useTemplatesGetAll = <DD = SurveyTemplatesTableData>(
  decorateData?: (data: PagingDataResponse<SurveyTemplate>) => DD,
): FetchGet<
  PagingDataResponse<SurveyTemplate>,
  PagingParams,
  DefaultFetchError,
  DD
> =>
  useFetchGet("survey-template", {
    autoStart: false,
    startStateLoading: false,
    decorateData,
  });

export const useTemplatesTableGet = () =>
  useTemplatesGetAll<SurveyTemplatesTableData>(
    ({
      data,
      meta,
    }: PagingDataResponse<SurveyTemplate>): SurveyTemplatesTableData => ({
      data: data.map(
        (item: SurveyTemplate): SurveyTemplateTableRow => ({
          key: item.id,
          id: item.id,
          title: item.title,
          userName:
            `${item.user?.firstName || ""} ${item.user?.lastName || ""}`.trim() ||
            "-",
          createdById: item.user?.id || "",
          createdAt: item.createdAt,
          status: item.status,
          updatedAt: item.updatedAt,
          user: item.user,
        }),
      ),
      total: meta.itemCount,
    }),
  );

export const useTemplateGetById = <DD = SurveyTemplate>(
  id?: string,
  decorateData?: (data: SurveyTemplate) => DD,
): FetchGetId<SurveyTemplate, DefaultFetchError, unknown, DD> =>
  useFetchGetId("survey-template", id, {
    autoStart: !!id,
    decorateData,
  });

export const useTemplateCreate = (): FetchCreate<
  SurveyTemplate,
  DefaultFetchError,
  SurveyCreateFullParams
> => useFetchCreate("survey-template");

export const useTemplateUpdate = (
  id?: string,
): FetchUpdate<
  SurveyTemplate,
  DefaultFetchError,
  Partial<SurveyCreateFullParams>
> => useFetchUpdate("survey-template", id);

export const useTemplateDelete = (
  id?: string,
): FetchDelete<FetchSuccess, DefaultFetchError> =>
  useFetchDelete("survey-template", id);

export const useTemplateCopy = () =>
  useFetchCreate<SurveyTemplate, DefaultFetchError, void>("survey-template");

export default {
  useSurveysGet,
  useSurveyCreateFull,
  useTemplateCreate,
  useTemplatesTableGet,
  useTemplateGetById,
};

export const useSurveyDownload = (
  id?: string,
  abortController?: AbortController,
): FetchGetId<ArrayBuffer> =>
  useFetchGetId(
    `survey${id ? `/${id}/pdf` : ""}`,
    "",
    {
      autoStart: false,
      startStateLoading: false,
      config: {
        headers: { "content-type": "multipart/form-data" },
        signal: abortController?.signal,
      },
    },
    "arraybuffer",
  );

export const useTempateDownload = (
  id?: string,
  abortController?: AbortController,
): FetchGetId<ArrayBuffer> =>
  useFetchGetId(
    `survey-template${id ? `/${id}/pdf` : ""}`,
    "",
    {
      autoStart: false,
      startStateLoading: false,
      config: {
        headers: { "content-type": "multipart/form-data" },
        signal: abortController?.signal,
      },
    },
    "arraybuffer",
  );
